import { useContext, useEffect, useState } from 'react';
import Highcharts from 'highcharts';

import AccountContext from 'contexts/AccountContext';
import { useChartPalette } from 'contexts/ChartPaletteContext';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';
import getGroupByField from 'hooks/useMetricMatrix/getGroupByField';
import useDateScope from 'hooks/useDateScope';
import useV5ChartData from 'hooks/useV5ChartData';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useValueFormatters from 'hooks/useValueFormatters';
import metricTypeCheckers from 'types/metricTypeCheckers';
import toBarSeries from '../toBarSeries';
import radarOptions from '../highchartOptions/radarOptions';

// Radar reuses the categories x series data produced by toBarSeries and renders
// each metric as a polar line series.
const useRadarData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
} => {
  const { unitsLocale, weekStartsOn } = useContext(AccountContext);
  const { weekStartsOnOverride } = useContext(WeekStartsOnOverrideContext);
  const { colors } = useChartPalette();
  const { openMenu } = useContext(DashboardContextMenuContext);

  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);
  const [groupField] = useState(getGroupByField(chartDefinition));

  const { formatMetric } = useValueFormatters();
  const dateScope = useDateScope({});
  const { data } = useV5ChartData(chartDefinition);
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    'radar',
    chartDefinition,
  );

  useEffect(() => {
    syncChart();
  }, [syncChart, onDrillDown, data]);

  useEffect(() => {
    if (!data) {
      setIsNoDataToDisplay(false);
      return;
    }

    const { series, xAxis } = toBarSeries(
      data,
      chartDefinition,
      false,
      unitsLocale,
      dateScope,
      11,
      weekStartsOnOverride ? weekStartsOnOverride : weekStartsOn,
      formatMetric,
      undefined,
      colors,
    );

    const categories = (xAxis?.categories as string[]) ?? [];

    const isNoData =
      series.length === 0 || !series.some((s) => s.data.length > 0);

    if (isNoData) {
      setIsNoDataToDisplay(true);
      setOptions(undefined);
      return;
    }

    const baseRadarOptions = radarOptions({
      title: ' ',
      openMenu,
      groupField,
      fieldType: chartDefinition.dimensionA
        ? chartDefinition.dimensionA.fieldType
        : undefined,
      chartDef: chartDefinition,
      fontSize: 11,
      formatMetric,
      showLegend: series.length > 1,
      dateScope,
    });

    setOptions({
      ...baseRadarOptions,
      xAxis: {
        ...baseRadarOptions.xAxis,
        categories,
      },
      series: series.map((s) => ({
        name: s.name,
        metricId: s.metricId,
        metricIds: metricTypeCheckers.isCompoundMetric(s.metric)
          ? s.metric.metricIds
          : [s.metricId],
        colorIndex: s.colorIndex,
        type: 'line' as const,
        onDrillDown,
        animation: false,
        data: s.data,
      })),
    } as any as Highcharts.Options);
    setIsNoDataToDisplay(false);
  }, [
    chartDefinition,
    data,
    dateScope,
    formatMetric,
    groupField,
    onDrillDown,
    openMenu,
    unitsLocale,
    colors,
    weekStartsOn,
    weekStartsOnOverride,
  ]);

  return { options, isNoData: isNoDataToDisplay };
};

export default useRadarData;
